import type {ButtonHTMLAttributes, CSSProperties, ReactNode} from 'react';

import {tapeColour, tilt} from './tilt';

// The tilt is per element, so it is passed as a CSS variable rather than a class.
const tiltStyle = (seed: string, max?: number) =>
    ({'--bt-tilt': `${tilt(seed, max)}deg`}) as CSSProperties;

export const StickerCard = ({
    seed,
    tape = true,
    className = '',
    children,
}: {
    seed: string;
    tape?: boolean;
    className?: string;
    children: ReactNode;
}) => (
    <div
        className={`bt-sticker ${className}`}
        data-tape={tape ? tapeColour(seed) : undefined}
        style={tiltStyle(seed)}
    >
        {children}
    </div>
);

export const Stamp = ({
    children,
    tone = 'stamp',
}: {
    children: ReactNode;
    tone?: 'stamp' | 'pink';
}) => <span className={`bt-stamp bt-stamp--${tone}`}>{children}</span>;

export const IVotedBadge = ({code}: {code: string}) => (
    <span className="bt-ivoted" aria-hidden="true">
        <span>
            I<br />
            VOTED<small>{code}</small>
        </span>
    </span>
);

export const PaperButton = ({
    variant = 'primary',
    className = '',
    ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {variant?: 'primary' | 'ghost'}) => (
    <button type="button" className={`bt-button bt-button--${variant} ${className}`} {...props} />
);

export const Toggle = ({
    checked,
    onChange,
    label,
    hint,
    disabled,
}: {
    checked: boolean;
    onChange: (next: boolean) => void;
    label: string;
    hint?: string;
    disabled?: boolean;
}) => (
    <label className="bt-toggle">
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
        />
        <span className="bt-toggle__switch" aria-hidden="true" />
        <span>
            <b>{label}</b>
            {hint && <span className="bt-toggle__hint">{hint}</span>}
        </span>
    </label>
);

export const Segmented = <T extends string>({
    value,
    options,
    onChange,
    columns,
    name,
    disabled,
}: {
    value: T;
    options: {value: T; label: string; hint?: string}[];
    onChange: (next: T) => void;
    columns: 2 | 3;
    name: string;
    disabled?: boolean;
}) => (
    <div className={`bt-segmented bt-segmented--${columns}`} role="radiogroup">
        {options.map((option) => (
            <label key={option.value} className="bt-segmented__option">
                <input
                    type="radio"
                    name={name}
                    checked={value === option.value}
                    disabled={disabled}
                    onChange={() => onChange(option.value)}
                />
                <span>
                    <b>{option.label}</b>
                    {option.hint && <span className="bt-segmented__hint">{option.hint}</span>}
                </span>
            </label>
        ))}
    </div>
);
